// Commands from the app (AD-11..AD-13, FR-19..FR-21): the app creates the command id, stamps intentAt with the clock
// offset from the API, shows set values provisionally, locks an action until its answer, and rolls back on rejection,
// expiry, stale, no answer within 10 s or a lost connection ("nicht bestätigt"). Never repeated automatically.
import {
  COMMAND_TIMEOUT_MS,
  isFinalAck,
  type AckDeltaMessage,
  type ClientMessage,
} from '@brewcore/app-contract';
import type { MachineStore, ParamValue } from './store.js';

export type CommandInput =
  | { kind: 'set'; params: Record<string, ParamValue>; origin?: 'ui' | 'ai' | 'restore' }
  | {
      kind: 'action';
      action: string;
      args?: Record<string, unknown>;
      expect?: Record<string, unknown>;
      confirm?: boolean;
    };

export type FinalResult =
  'done' | 'failed' | 'rejected' | 'expired' | 'stale' | 'no_response' | 'unconfirmed';

export interface CommandOutcome {
  id: string;
  result: FinalResult;
  reason?: string;
  effective?: Record<string, ParamValue>;
  /** when a done set takes effect (FR-20: own answers for after the shot / after a restart) */
  applies?: 'now' | 'after_brew' | 'after_restart';
  /** the answer came from the API (rights, offline, limits) or the machine */
  source: 'api' | 'machine' | 'app';
}

interface Open {
  id: string;
  machine: string;
  input: CommandInput;
  accepted: boolean;
  timer: ReturnType<typeof setTimeout>;
  resolve: (o: CommandOutcome) => void;
  store: MachineStore;
}

/**
 * After "accepted" the machine runs the action (a flush, a programme of about an hour, a scale search); the answer
 * done follows when it ends. The 10-s "keine Rückmeldung" no longer applies (FR-20); the API gives up after 2 h.
 */
export const ACCEPTED_MAX_MS = 2 * 3600_000;

export function newCommandId(): string {
  return globalThis.crypto.randomUUID().replaceAll('-', '');
}

export class CommandSender {
  private readonly open = new Map<string, Open>();
  private readonly listeners = new Set<() => void>();
  private readonly finals = new Set<
    (machine: string, input: CommandInput, outcome: CommandOutcome) => void
  >();

  constructor(
    private readonly transport: { send(msg: ClientMessage): boolean },
    private readonly clock: { nowMs(): number },
    private readonly timeoutMs = COMMAND_TIMEOUT_MS,
  ) {}

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Every final outcome (the UI shows "Gespeichert" or the reason in one place). */
  onFinal(fn: (machine: string, input: CommandInput, outcome: CommandOutcome) => void): () => void {
    this.finals.add(fn);
    return () => this.finals.delete(fn);
  }

  private changed(): void {
    for (const fn of this.listeners) fn();
  }

  /** Name of an action currently waiting for its answer (the button stays locked, AD-12). */
  isLocked(machine: string, action: string): boolean {
    for (const o of this.open.values())
      if (o.machine === machine && o.input.kind === 'action' && o.input.action === action) return true;
    return false;
  }

  openCommands(machine: string): { id: string; input: CommandInput; accepted: boolean }[] {
    return [...this.open.values()]
      .filter((o) => o.machine === machine)
      .map((o) => ({ id: o.id, input: o.input, accepted: o.accepted }));
  }

  send(store: MachineStore, input: CommandInput, id: string = newCommandId()): Promise<CommandOutcome> {
    const machine = store.machineId;
    if (input.kind === 'action' && this.isLocked(machine, input.action)) {
      return Promise.resolve({ id, result: 'rejected', reason: 'pending', source: 'app' });
    }
    const intentAt = Math.floor(this.clock.nowMs() / 1000);
    const msg: ClientMessage =
      input.kind === 'set'
        ? {
            t: 'cmd',
            machine,
            id,
            intentAt,
            kind: 'set',
            params: input.params,
            ...(input.origin ? { origin: input.origin } : {}),
          }
        : {
            t: 'cmd',
            machine,
            id,
            intentAt,
            kind: 'action',
            action: input.action,
            ...(input.args ? { args: input.args } : {}),
            ...(input.expect ? { expect: input.expect } : {}),
            ...(input.confirm !== undefined ? { confirm: input.confirm } : {}),
          };
    return new Promise<CommandOutcome>((resolve) => {
      if (input.kind === 'set') store.setProvisional(input.params, id);
      const timer = setTimeout(
        () => this.finish(id, { id, result: 'no_response', source: 'app' }),
        this.timeoutMs,
      );
      this.open.set(id, { id, machine, input, accepted: false, timer, resolve, store });
      this.changed();
      // no connection, or reconnected but the snapshot is not there yet (FR-21: the current state before control):
      // not sent, answered at once (the screen keeps controls during short gaps, core/calm.ts). Rights are the API's.
      const v = store.getSnapshot();
      if ((!v.ready && v.linkLostAt !== null) || !this.transport.send(msg))
        this.finish(id, { id, result: 'unconfirmed', reason: 'offline', source: 'app' });
    });
  }

  onAck(ack: AckDeltaMessage): void {
    const o = this.open.get(ack.id);
    if (!o) return;
    if (ack.result === 'accepted') {
      o.accepted = true;
      clearTimeout(o.timer);
      o.timer = setTimeout(
        () => this.finish(o.id, { id: o.id, result: 'no_response', source: 'app' }),
        ACCEPTED_MAX_MS,
      );
      this.changed();
      return;
    }
    if (!isFinalAck(ack.result)) return;
    this.finish(
      ack.id,
      {
        id: ack.id,
        result: ack.result as FinalResult,
        ...(ack.reason ? { reason: ack.reason } : {}),
        ...(ack.effective ? { effective: ack.effective } : {}),
        ...(ack.applies ? { applies: ack.applies } : {}),
        source: ack.source,
      },
      ack,
    );
  }

  /** Lost connection: nothing open can be confirmed any more (the next snapshot shows the truth, FR-21). */
  onDisconnected(): void {
    for (const id of [...this.open.keys()]) this.finish(id, { id, result: 'unconfirmed', source: 'app' });
  }

  private finish(id: string, outcome: CommandOutcome, ack?: AckDeltaMessage): void {
    const o = this.open.get(id);
    if (!o) return;
    clearTimeout(o.timer);
    this.open.delete(id);
    if (outcome.result === 'done' && o.input.kind === 'set')
      o.store.confirmProvisional(id, ack?.effective, ack?.exec);
    else if (o.input.kind === 'set') o.store.rollback(id);
    o.resolve(outcome);
    for (const fn of this.finals) fn(o.machine, o.input, outcome);
    this.changed();
  }
}
